import { graphql, readInlineData } from 'relay-runtime';
import {
    getDefaultCountry,
    wasPaymentDenied,
    getAddressErrors,
    getBillingAddressFields,
} from '../helpers/checkoutForms';
import { TABS, STATES, METHODS, ERRORS } from '../constants/checkoutConstants';
import {
    defaultFormValue,
    defaultBlurred as achDefaultBlurred,
} from 'dibs-ach-form/exports/ACHForm';
import achValidate from 'dibs-ach-form/exports/ACHValidate';
import {
    addressSelectedAction,
    setProcessingErrorAction,
    emptyShippingAddressAction,
    emptyPaymentMethodAction,
    saveCreditCardAction,
    saveBankAccountAction,
    savePaypalAction,
    saveKlarnaPaymentMethodAction,
    completeCreditCardAction,
    completeBankAccountAction,
    navigateShippingAction,
    submitKlarnaError,
} from '../actions/commonActions';
import { transactionRecomputedAction } from '../actions/checkoutRelayAction';

const initializeCreditCardState = state => ({
    ...state,
    newCardForm: { cardHolderName: state.defaultCardHolderName },
    newBillingForm: { country: state.defaultCountry },
    verifyCardForm: {},
    verifyCardToken: null,
    creditCardError: null,
    hasShownCreditCardError: false,
    newEoriNumber: null,
    newVatId: null,
});

const initializeBankAccountState = state => {
    return {
        ...state,
        newACHForm: defaultFormValue,
        newBillingForm: { country: state.defaultCountry },
        bankAccountError: null,
        hasShownBankAccountError: false,
        achFormBlurred: achDefaultBlurred,
        achShowFailedSubmissionError: false,
        wireShowFailedSubmissionError: false,
        bankAccountMethodSelected: 'bankAccount',
        bankAccountSectionSelected: null,
        verifyBankAccountForm: defaultFormValue,
        verifyBankAccountToken: null,
        newEoriNumber: null,
        newVatId: null,
    };
};

const initializePayPalState = state => ({
    ...state,
    payPalError: null,
    hasShownPayPalError: false,
});

const initializeFormState = state => ({
    ...initializeCreditCardState(state),
    ...initializePayPalState(state),
    ...initializeBankAccountState(state),
});

const initialKlarnaState = {
    klarnaSessionId: null,
    klarnaClientToken: null,
    klarnaNeedsSessionUpdate: false,
    klarnaNeedsReauthorization: false,
    klarnaNeedsFinalization: false,
    klarnaCurrentAuthorizedInvoice: null,
    klarnaFailedReauthorization: false,
};

const initialState = {
    paymentTab: TABS.TAB_CREDIT_CARD,
    newCardForm: {},
    newACHForm: defaultFormValue,
    newBillingForm: {},
    verifyCardForm: {},
    verifyBankAccountForm: defaultFormValue,
    lastValidCreditCardToken: null,
    lastValidBankAccountToken: null,
    isBillingSameAsShipping: true,
    isPaymentSaving: false,
    isPayPalSaving: false,
    isPayPalRemoving: false,
    deselectCreditCards: false,
    isCreditCardListExpanded: false,
    achFormBlurred: achDefaultBlurred,
    achShowFailedSubmissionError: false,
    wireShowFailedSubmissionError: false,
    bankAccountMethodSelected: 'bankAccount',
    lastKnownWireOrCheck: 'wireTransfer',
    bankAccountSectionSelected: null,
    deselectBankAccounts: false,
    isBankAccountListExpanded: false,
    applePayAvailable: !!window.ApplePaySession && !!window.ApplePaySession.canMakePayments(),
    newEoriNumber: null,
    newVatId: null,
    buyerInvoicePromotionAmount: 0,
    paymentError: null,
    ...initialKlarnaState,
};

export const isAddingCreditCard = state => state.creditCardState === STATES.STATE_NEW;
export const isVerifyingCreditCard = state => state.creditCardState === STATES.STATE_VERIFY;
export const hasSelectedCreditCardTab = state => state.paymentTab === TABS.TAB_CREDIT_CARD;
export const hasSelectedPayPalTab = state => state.paymentTab === TABS.TAB_PAYPAL_ACCOUNT;
export const hasSelectedBankAccountTab = state => state.paymentTab === TABS.TAB_BANK_ACCOUNT;

export const isAddingBankAccount = state => state.bankAccountState === STATES.STATE_NEW;
export const isVerifyingBankAccount = state => state.bankAccountState === STATES.STATE_VERIFY;

export const isBillingAddressFormValid = state => {
    return state.isBillingSameAsShipping
        ? true
        : getAddressErrors(state.newBillingForm, true).isValid;
};

export const isEntireACHFormValid = state => {
    const relevantForm = isVerifyingBankAccount(state)
        ? state.verifyBankAccountForm
        : state.newACHForm;

    return achValidate(relevantForm).isValid && isBillingAddressFormValid(state);
};

const getDefaultBankAccountToken = (buyer, shippingAddressId) => {
    const validBankAccounts = (buyer?.wallet?.paymentMethods || []).filter(
        ({ paymentMethod, shippingAddresses = [] }) =>
            paymentMethod.apiType === METHODS.METHOD_ACH &&
            shippingAddresses.find(({ serviceId }) => serviceId === shippingAddressId)
    );
    return validBankAccounts?.[0]?.paymentMethod?.token || null;
};

function getDefaultShippingAddressId(buyer, preCheckoutTransaction) {
    const preCheckoutAddressId = preCheckoutTransaction?.shipment?.shippingAddress?.sourceId;
    const personalAddress = buyer?.addressBook?.personalAddress;
    const isPersonalAddressValid = personalAddress?.validation?.buyerShipping?.valid;
    const addressList = buyer?.addressBook?.addressList || [];
    const shippingAddress =
        addressList.find(
            ({ serviceId, validation }) =>
                serviceId === preCheckoutAddressId && !!validation?.buyerShipping?.valid
        ) ||
        (isPersonalAddressValid && personalAddress) ||
        buyer?.firstValidShippingAddress;
    return shippingAddress?.serviceId || null;
}

function buildInvoiceForComparison(buyerInvoice, buyerInvoicePromotionAmount) {
    const invoice = {};
    for (const item of buyerInvoice?.invoiceItems || []) {
        const code = item?.code;
        const amount = item?.totalAmount?.amount;
        if (code) {
            invoice[code] = amount;
        }
    }

    invoice.PROMOTION_AMOUNT =
        buyerInvoicePromotionAmount !== undefined ? buyerInvoicePromotionAmount : 0;

    return invoice;
}

function calculateIfInvoiceChanged(oldInvoice, newInvoice) {
    const oldCodes = oldInvoice ? Object.keys(oldInvoice) : [];
    const newCodes = newInvoice ? Object.keys(newInvoice) : [];

    if (oldCodes.length !== newCodes.length) {
        return true;
    }

    return oldCodes.some(code => oldInvoice[code] !== newInvoice[code]);
}

export function checkoutPaymentReducer(state = initialState, action = {}) {
    switch (action.type) {
        case 'CHECKOUT_BUYER_ITEM_VIEWER_UPDATE': {
            // should run at most 2 times
            // 1) first checkout page load, with or without a buyer based on log in status
            // 2) when a logged out user logs in
            const { buyer: buyerRef, preCheckoutTransaction: preCheckoutTransactionRef } =
                action.payload;
            const buyer = readInlineData(
                graphql`
                    fragment checkoutPaymentReducer_buyer on User @inline {
                        serviceId
                        profile {
                            displayName(preventEmailFallback: true)
                            country
                        }
                        firstValidShippingAddress {
                            country
                        }
                        isTrade: isVerifiedTrade
                        wallet {
                            hasCreditCards
                            hasBankAccounts
                            paymentMethods {
                                paymentMethod {
                                    __typename
                                    ... on CreditCard {
                                        token
                                    }
                                    ... on ACH {
                                        token
                                    }
                                }
                                shippingAddresses {
                                    serviceId
                                }
                            }
                        }
                    }
                `,
                buyerRef
            );
            const preCheckoutTransaction = readInlineData(
                graphql`
                    fragment checkoutPaymentReducer_preCheckoutTransaction on Transaction @inline {
                        paymentAuthorizations {
                            status
                            paymentMethod {
                                ... on Unknown {
                                    billingAddress {
                                        ...checkoutForms_address
                                    }
                                }
                            }
                        }
                    }
                `,
                preCheckoutTransactionRef
            );
            const defaultCountry = buyer?.firstValidShippingAddress?.country || getDefaultCountry();
            const cardHolderName = buyer?.profile?.displayName || '';
            const hasCreditCards = buyer?.wallet?.hasCreditCards;
            const hasBankAccounts = buyer?.wallet?.hasBankAccounts;
            const newPaymentAuth = (preCheckoutTransaction?.paymentAuthorizations || []).find(
                pa => pa.status === 'NEW'
            );
            const billingAddress = getBillingAddressFields(
                newPaymentAuth?.paymentMethod?.billingAddress || null
            );

            const shippingAddressId = getDefaultShippingAddressId(buyer, preCheckoutTransaction);

            return {
                ...state,
                defaultCountry: billingAddress?.country || defaultCountry,
                defaultCardHolderName: cardHolderName,
                creditCardState: hasCreditCards ? STATES.STATE_SAVED : STATES.STATE_NEW,
                bankAccountState: hasBankAccounts ? STATES.STATE_SAVED : STATES.STATE_NEW,
                newCardForm: { cardHolderName },
                newBillingForm: billingAddress,
                verifyCardForm: {},
                verifyBankAccountForm: defaultFormValue,
                verifyCardToken: null,
                verifyBankAccountToken: null,
                hasShownCreditCardError: false,
                hasShownPayPalError: false,
                hasShownBankAccountError: false,
                defaultBankAccountToken: getDefaultBankAccountToken(buyer, shippingAddressId),
                isBillingSameAsShipping: !buyer?.isTrade,
            };
        }
        case emptyShippingAddressAction.toString(): {
            return {
                ...initializeCreditCardState(state),
                creditCardState: STATES.STATE_NEW,
                bankAccountState: STATES.STATE_NEW,
                lastValidCreditCardToken: null,
                lastValidBankAccountToken: null,
            };
        }
        case addressSelectedAction.toString(): {
            const { klarnaSessionId } = state;
            const { paymentMethodToken } = action.payload;
            return {
                ...initializeCreditCardState(state),
                ...initializeBankAccountState(state),
                bankAccountMethodSelected: state.bankAccountMethodSelected,
                paymentTab: state.paymentTab,
                lastValidCreditCardToken: paymentMethodToken,
                lastValidBankAccountToken: null,
                klarnaNeedsReauthorization: !!klarnaSessionId,
                klarnaNeedsSessionUpdate: !!klarnaSessionId,
            };
        }
        case 'CHECKOUT_PANE_CHANGED': {
            const { hasCreditCards, hasBankAccounts } = action;
            return {
                ...state,
                bankAccountState: hasBankAccounts ? STATES.STATE_SAVED : STATES.STATE_NEW,
                creditCardState: hasCreditCards ? STATES.STATE_SAVED : STATES.STATE_NEW,
                verifyCardToken: null,
                verifyBankAccountToken: null,
            };
        }
        case 'CHECKOUT_SELECT_PAYMENT_TAB': {
            const {
                paymentTab,
                lastValidCreditCardToken,
                lastValidBankAccountToken,
                deselectCreditCards = false,
                deselectBankAccounts = false,
            } = action;

            return {
                ...state,
                paymentTab,
                lastValidCreditCardToken,
                lastValidBankAccountToken:
                    lastValidBankAccountToken ||
                    (!isVerifyingBankAccount(state) ? state.defaultBankAccountToken : null),
                deselectCreditCards:
                    deselectCreditCards && paymentTab !== METHODS.METHOD_CREDIT_CARD,
                deselectBankAccounts: deselectBankAccounts && paymentTab !== METHODS.METHOD_ACH,
                newBillingForm: {},
            };
        }
        case 'CHECKOUT_NEW_CREDITCARD': {
            return {
                ...state,
                creditCardState: STATES.STATE_NEW,
            };
        }
        case 'CHECKOUT_SELECT_CREDITCARD': {
            const { creditCardState, token } = action;
            return {
                ...initializeCreditCardState(state),
                creditCardState,
                lastValidCreditCardToken: token,
            };
        }
        case 'CHECKOUT_VERIFY_CREDITCARD': {
            const { token, verifyCardForm } = action;
            return {
                ...state,
                creditCardState: STATES.STATE_VERIFY,
                verifyCardToken: token,
                verifyCardForm,
            };
        }
        case saveCreditCardAction.toString(): {
            const { token } = action.payload;
            return {
                ...initializeCreditCardState(state),
                creditCardState: STATES.STATE_SAVED,
                lastValidCreditCardToken: token,
            };
        }
        case 'CHECKOUT_NEW_BANKACCOUNT': {
            return {
                ...state,
                bankAccountState: STATES.STATE_NEW,
                achFormBlurred: {
                    accountType: false,
                    ownershipType: false,
                    routingNumber: false,
                    accountNumber: false,
                    firstNameOnAccount: false,
                    lastNameOnAccount: false,
                    businessNameOnAccount: false,
                },
                achShowFailedSubmissionError: false,
            };
        }
        case saveBankAccountAction.toString(): {
            const { token } = action.payload;
            return {
                ...initializeBankAccountState(state),
                bankAccountMethodSelected: 'bankAccount',
                bankAccountState: STATES.STATE_SAVED,
                lastValidBankAccountToken: token,
            };
        }
        case 'CHECKOUT_CHANGE_BANKACCOUNT_SECTION': {
            const { bankAccountSectionSelected } = action;
            const bankAccountMethodSelected =
                bankAccountSectionSelected === 'all' ? 'bankAccount' : state.lastKnownWireOrCheck;
            return {
                ...state,
                bankAccountSectionSelected,
                bankAccountMethodSelected,
            };
        }
        case 'CHECKOUT_BANK_ACCOUNT_WIRE_OR_CHECK': {
            const { bankAccountMethodSelected } = action;
            return {
                ...state,
                bankAccountMethodSelected,
                lastKnownWireOrCheck: bankAccountMethodSelected,
                wireShowFailedSubmissionError: false,
            };
        }
        case completeCreditCardAction.toString(): {
            const { paymentMethodToken } = action.payload;
            return {
                ...state,
                creditCardState: STATES.STATE_SAVED,
                lastValidCreditCardToken: paymentMethodToken,
            };
        }
        case 'SAVE_APPLE_PAY_PAYMENT_REQUEST': {
            const { applePayPaymentRequest } = action;
            return {
                ...state,
                applePayPaymentRequest,
            };
        }
        case 'CHECKOUT_CANCEL_CREDITCARD': {
            return {
                ...initializeCreditCardState(state),
                creditCardState: STATES.STATE_SAVED,
            };
        }
        case 'CHECKOUT_CANCEL_BANKACCOUNT': {
            return {
                ...initializeBankAccountState(state),
                bankAccountMethodSelected: state.bankAccountMethodSelected,
                bankAccountState: STATES.STATE_SAVED,
            };
        }
        case emptyPaymentMethodAction.toString(): {
            const {
                method,
                creditCardState = state.creditCardState,
                bankAccountState = state.bankAccountState,
            } = action.payload;
            if (method === METHODS.METHOD_CREDIT_CARD || method === METHODS.METHOD_ACH) {
                return {
                    ...initializeFormState(state),
                    bankAccountMethodSelected: state.bankAccountMethodSelected,
                    creditCardState,
                    bankAccountState,
                    lastValidCreditCardToken:
                        method === METHODS.METHOD_ACH ? state.lastValidCreditCardToken : null,
                    lastValidBankAccountToken:
                        method === METHODS.METHOD_CREDIT_CARD
                            ? state.lastValidBankAccountToken
                            : null,
                };
            }
            return state;
        }
        case 'CHECKOUT_CREDITCARD_ERROR': {
            const { creditCardState } = state;
            const { error } = action;

            // we only account for unsuccessful payment verification (status: 502)
            return {
                ...state,
                creditCardState: isVerifyingCreditCard(state)
                    ? STATES.STATE_SAVED
                    : creditCardState,
                creditCardError: !!error && ERRORS.ERROR_VAULT,
            };
        }
        case 'CHECKOUT_BANKACCOUNT_ERROR': {
            const { bankAccountState } = state;
            const { error } = action;

            return {
                ...state,
                bankAccount: isVerifyingBankAccount(state) ? STATES.STATE_SAVED : bankAccountState,
                bankAccountError: error || 'DEFAULT',
            };
        }
        case 'CHECKOUT_PAYMENT_ERROR_SHOWN': {
            return {
                ...state,
                hasShownCreditCardError:
                    state.paymentTab === TABS.TAB_CREDIT_CARD || state.hasShownCreditCardError,
                hasShownPayPalError:
                    state.paymentTab === TABS.TAB_PAYPAL_ACCOUNT || state.hasShownPayPalError,
                hasShownBankAccountError:
                    state.paymentTab === TABS.TAB_BANK_ACCOUNT || state.hasShownBankAccountError,
            };
        }
        case 'CHECKOUT_PAYMENT_ERROR': {
            const { error } = action;
            return {
                ...state,
                paymentError: error,
            };
        }
        case setProcessingErrorAction.toString(): {
            const { errorStatus, errorMessage } = action.payload;
            const {
                paymentTab,
                creditCardError,
                payPalError,
                hasShownCreditCardError,
                hasShownPayPalError,
                bankAccountError,
                hasShownBankAccountError,
            } = state;
            const isCreditCard = paymentTab === TABS.TAB_CREDIT_CARD;
            const isPayPalAccount = paymentTab === TABS.TAB_PAYPAL_ACCOUNT;
            const isBankAccount = paymentTab === TABS.TAB_BANK_ACCOUNT;

            if (wasPaymentDenied({ errorStatus, errorMessage })) {
                return {
                    ...state,
                    creditCardError: isCreditCard ? ERRORS.ERROR_DENIED : creditCardError,
                    hasShownCreditCardError: isCreditCard ? false : hasShownCreditCardError,
                    payPalError: isPayPalAccount ? ERRORS.ERROR_DENIED : payPalError,
                    hasShownPayPalError: isPayPalAccount ? false : hasShownPayPalError,
                    bankAccountError: isBankAccount ? ERRORS.ERROR_DENIED : bankAccountError,
                    hasShownBankAccountError: isBankAccount ? false : hasShownBankAccountError,
                };
            }
            return state;
        }
        case 'CHECKOUT_UPDATE_NEW_CARD_FORM': {
            const { field, value } = action;
            return {
                ...state,
                newCardForm: { ...state.newCardForm, [field]: value },
            };
        }
        case 'CHECKOUT_UPDATE_ACH_FORM_BLUR': {
            const { newBlurredValues } = action;
            return {
                ...state,
                achFormBlurred: newBlurredValues,
            };
        }
        case 'CHECKOUT_UPDATE_NEW_BILLING_FORM': {
            const { field, value } = action;
            return {
                ...state,
                newBillingForm: { ...state.newBillingForm, [field]: value },
                achShowFailedSubmissionError:
                    state.achShowFailedSubmissionError && !isEntireACHFormValid(state),
                wireShowFailedSubmissionError:
                    state.wireShowFailedSubmissionError && !isBillingAddressFormValid(state),
            };
        }
        case 'CHECKOUT_UPDATE_VERIFY_CARD_FORM': {
            const { field, value } = action;
            return {
                ...state,
                verifyCardForm: { ...state.verifyCardForm, [field]: value },
            };
        }
        case 'CHECKOUT_UPDATE_VERIFY_BANKACCOUNT_FORM': {
            const { field, value } = action;
            return {
                ...state,
                verifyBankAccountForm: { ...state.verifyBankAccountForm, [field]: value },
                isBillingSameAsShipping: false,
                achShowFailedSubmissionError:
                    state.achShowFailedSubmissionError && !isEntireACHFormValid(state),
            };
        }
        case navigateShippingAction.toString(): {
            return {
                ...initializeFormState(state),
                bankAccountMethodSelected: state.bankAccountMethodSelected,
            };
        }
        case 'CHECHKOUT_NAVIGATE_DELIVERY': {
            return initializeFormState(state);
        }
        case savePaypalAction.toString(): {
            return initializePayPalState(state);
        }
        case 'PAYMENT_RESET_PAYPAL': {
            return initializePayPalState(state);
        }
        case 'PAYMENT_PAYPAL_ERROR': {
            const { error } = action;
            return {
                ...state,
                payPalError: error,
            };
        }
        case 'CHECKOUT_UPDATE_ACH_FORM': {
            const { field, value } = action;
            return {
                ...state,
                newACHForm: { ...state.newACHForm, [field]: value },
                achShowFailedSubmissionError:
                    state.achShowFailedSubmissionError && !isEntireACHFormValid(state),
            };
        }
        case 'CHECKOUT_SELECT_BANKACCOUNT': {
            const { bankAccountState, token } = action;
            return {
                ...initializeBankAccountState(state),
                bankAccountMethodSelected: state.bankAccountMethodSelected,
                bankAccountState,
                lastValidBankAccountToken: token,
            };
        }
        case 'CHECKOUT_VERIFY_BANKACCOUNT': {
            const { token, verifyBankAccountForm } = action;
            return {
                ...state,
                bankAccountState: STATES.STATE_VERIFY,
                verifyBankAccountToken: token,
                verifyBankAccountForm: { ...state.verifyBankAccountForm, ...verifyBankAccountForm },
            };
        }
        case 'TOGGLE_ACH_FORM_SUBMISSION_ERROR': {
            const { showError } = action;
            return {
                ...state,
                achShowFailedSubmissionError: showError,
            };
        }
        case 'TOGGLE_WIRE_FORM_SUBMISSION_ERROR': {
            const { showError } = action;
            return {
                ...state,
                wireShowFailedSubmissionError: showError,
            };
        }
        case completeBankAccountAction.toString(): {
            const { paymentMethodToken } = action.payload;
            return {
                ...state,
                bankAccountState: STATES.STATE_SAVED,
                lastValidBankAccountToken: paymentMethodToken,
            };
        }
        case 'CHECKOUT_TOGGLE_BILLING_ADDRESS': {
            return {
                ...state,
                isBillingSameAsShipping: action.checked,
                achShowFailedSubmissionError:
                    state.achShowFailedSubmissionError &&
                    !isEntireACHFormValid({ ...state, isBillingSameAsShipping: action.checked }),
                wireShowFailedSubmissionError:
                    state.wireShowFailedSubmissionError &&
                    !isBillingAddressFormValid({
                        ...state,
                        isBillingSameAsShipping: action.checked,
                    }),
            };
        }

        case 'CHECKOUT_PAYMENT_SAVING': {
            return {
                ...state,
                isPaymentSaving: action.isSaving,
            };
        }

        case 'CHECKOUT_PAYPAL_SAVING': {
            return {
                ...state,
                isPayPalSaving: action.isSaving,
            };
        }

        case 'CHECKOUT_PAYPAL_REMOVING': {
            return {
                ...state,
                isPayPalRemoving: action.isRemoving,
            };
        }

        case 'TOGGLE_CREDIT_CARD_LIST_EXPANDED': {
            return {
                ...state,
                isCreditCardListExpanded: !state.isCreditCardListExpanded,
            };
        }

        case 'TOGGLE_BANKACCOUNT_LIST_EXPANDED': {
            return {
                ...state,
                isBankAccountListExpanded: !state.isBankAccountListExpanded,
            };
        }
        case 'CHECKOUT_VAT_ID_UPDATED': {
            const { value } = action;
            return {
                ...state,
                newVatId: value,
            };
        }
        case 'CHECKOUT_EORI_NUMBER_UPDATED': {
            const { value } = action;
            return {
                ...state,
                newEoriNumber: value,
            };
        }
        case 'SAVE_KLARNA_SESSION': {
            const { sessionId, buyerInvoice } = action;
            return {
                ...state,
                klarnaSessionId: sessionId,
                klarnaCurrentAuthorizedInvoice: buyerInvoice
                    ? buildInvoiceForComparison(buyerInvoice, state.buyerInvoicePromotionAmount)
                    : null,
            };
        }
        case 'SAVE_KLARNA_CLIENT_TOKEN': {
            const { token } = action;
            return {
                ...state,
                klarnaClientToken: token,
            };
        }
        case 'SCHEDULE_KLARNA_REAUTHORIZATION': {
            return {
                ...state,
                klarnaNeedsReauthorization: true,
            };
        }
        case saveKlarnaPaymentMethodAction.toString(): {
            const { klarnaNeedsFinalization, buyerInvoice } = action.payload;
            return {
                ...state,
                klarnaNeedsFinalization,
                klarnaNeedsReauthorization: false,
                klarnaCurrentAuthorizedInvoice: buildInvoiceForComparison(
                    buyerInvoice,
                    state.buyerInvoicePromotionAmount
                ),
            };
        }
        case 'KLARNA_SESSION_UPDATED': {
            return {
                ...state,
                klarnaNeedsSessionUpdate: false,
            };
        }
        case submitKlarnaError.toString(): {
            return {
                ...state,
                klarnaFailedReauthorization: true,
            };
        }
        case transactionRecomputedAction.toString(): {
            // todo create inline fragment on transaction
            const transaction = action?.payload?.transaction || {};
            const { buyerInvoice, isKlarnaEligible } = transaction;
            const buyerCurrentInvoicePromotionAmount =
                transaction?.summary?.invoiceSummary?.buyerCurrentInvoicePromotionTotal?.amount;

            const { klarnaSessionId, klarnaCurrentAuthorizedInvoice, klarnaNeedsReauthorization } =
                state;

            if (!klarnaSessionId) {
                return {
                    ...state,
                    buyerInvoicePromotionAmount: buyerCurrentInvoicePromotionAmount,
                };
            }

            if (klarnaSessionId && !isKlarnaEligible) {
                return {
                    ...state,
                    ...initialKlarnaState,
                    paymentTab: initialState.paymentTab,
                    buyerInvoicePromotionAmount: buyerCurrentInvoicePromotionAmount,
                };
            }

            const newInvoice = buildInvoiceForComparison(
                buyerInvoice,
                buyerCurrentInvoicePromotionAmount
            );

            const hasInvoiceChanged = calculateIfInvoiceChanged(
                klarnaCurrentAuthorizedInvoice,
                newInvoice
            );

            return {
                ...state,
                klarnaNeedsReauthorization: klarnaNeedsReauthorization || hasInvoiceChanged,
                klarnaNeedsSessionUpdate: hasInvoiceChanged,
                klarnaCurrentAuthorizedInvoice: newInvoice,
                buyerInvoicePromotionAmount: buyerCurrentInvoicePromotionAmount,
            };
        }
    }
    return state;
}
